import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import { signPdf } from '../../services/signPdfApi.js';
import { rememberRecentApp } from '../../utils/recentApps.js';
import {
  FiAlertCircle,
  FiArrowDown,
  FiArrowLeft,
  FiArrowRight,
  FiArrowUp,
  FiCheck,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiEdit2,
  FiEdit3,
  FiFileText,
  FiImage,
  FiInfo,
  FiMove,
  FiRotateCcw,
  FiTrash2,
  FiType,
  FiX
} from 'react-icons/fi';
import ThemeToggle from '../ThemeToggle.jsx';
import TourButton from '../tour/TourButton.jsx';
import HelpMenu, { FeedbackButton } from '../help/HelpMenu.jsx';
import TourMode from '../tour/TourMode.jsx';
import { usePageTour } from '../tour/usePageTour.js';
import FileDrop from './FileDrop.jsx';
import '../../styles/sign-pdf.css';

// Stable public URL — avoids Vite-hashed worker paths that break on deploy MIME/CDN.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.min.mjs`;

const SIG_STORAGE_KEY = 'novabotx_last_signature_png';
const MAX_PDF_MB = 15;

const SIGN_TOUR_STEPS = [
  {
    target: 'sign-upload',
    title: '1 · Add your PDF',
    body: `Click or drag a PDF (up to ${MAX_PDF_MB}MB) here. Multi-page files get page arrows so you can sign any page.`
  },
  {
    target: 'sign-signature',
    title: '2 · Create a signature',
    body: 'Draw it with your mouse or finger, type your name, or upload an image — then press “Use signature”. We remember it for next time on this device.'
  },
  {
    target: 'sign-adjust',
    title: '3 · Fine-tune it',
    body: 'Resize and tilt the stamp with the sliders, and nudge it pixel-by-pixel with the arrow buttons.'
  },
  {
    target: 'sign-preview',
    title: 'Place it on the page',
    body: 'Your PDF renders here. Drag the signature to move it, or click anywhere on the page to drop it there.'
  },
  {
    target: 'sign-apply',
    title: 'Apply & download',
    body: 'When it looks right, this stamps the signature onto the PDF and downloads the signed copy.'
  }
];

function loadSavedSignature() {
  try {
    return localStorage.getItem(SIG_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function clearSavedSignature() {
  try {
    localStorage.removeItem(SIG_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function saveSignatureDataUrl(dataUrl) {
  try {
    localStorage.setItem(SIG_STORAGE_KEY, dataUrl);
  } catch {
    // ignore
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load signature image'));
    img.src = src;
  });
}

/** Make near-white / light pixels transparent so only ink remains over the PDF. */
async function makeInkTransparent(dataUrl, threshold = 230) {
  const img = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.width || 1;
  canvas.height = img.height || 1;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    if (a === 0) continue;
    // Drop bright / near-white fills (and light gray anti-alias fringe)
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    if (luminance >= threshold || (r >= threshold && g >= threshold && b >= threshold)) {
      data[i + 3] = 0;
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
}

/**
 * Bake rotation into a PNG so preview (CSS rotate around center) matches PDF output.
 * Returns blob + preview-pixel size of the axis-aligned bounding box.
 */
async function bakeRotatedSignature(dataUrl, widthPx, heightPx, angleDeg) {
  const img = await loadImage(dataUrl);
  const rad = (Number(angleDeg) || 0) * (Math.PI / 180);
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const bw = Math.max(1, Math.ceil(widthPx * cos + heightPx * sin));
  const bh = Math.max(1, Math.ceil(widthPx * sin + heightPx * cos));
  const canvas = document.createElement('canvas');
  canvas.width = bw;
  canvas.height = bh;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, bw, bh);
  ctx.translate(bw / 2, bh / 2);
  ctx.rotate(rad);
  ctx.drawImage(img, -widthPx / 2, -heightPx / 2, widthPx, heightPx);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not export signature'))), 'image/png');
  });
  return { blob, width: bw, height: bh };
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export default function SignPdfPage() {
  const navigate = useNavigate();
  const { touring, start: startTour, end: endTour } = usePageTour();
  const canvasRef = useRef(null);
  const drawPadRef = useRef(null);
  const pdfDocRef = useRef(null);
  const drawingRef = useRef(false);

  const [pdfFile, setPdfFile] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 });
  const [mode, setMode] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState(() => loadSavedSignature());
  const [stamp, setStamp] = useState({ x: 40, y: 40, width: 180, height: 64, angle: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  // Strip white fill from any previously saved signature (localStorage).
  useEffect(() => {
    const saved = loadSavedSignature();
    if (!saved) return undefined;
    let cancelled = false;
    makeInkTransparent(saved)
      .then((clean) => {
        if (cancelled || clean === saved) return;
        setSignatureDataUrl(clean);
        saveSignatureDataUrl(clean);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const clearDrawPad = useCallback(() => {
    const canvas = drawPadRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  useEffect(() => {
    if (mode === 'draw' && drawPadRef.current) {
      clearDrawPad();
    }
  }, [mode, clearDrawPad]);

  const renderPage = useCallback(async (doc, pageNumber) => {
    const pdfPage = await doc.getPage(pageNumber);
    const unscaled = pdfPage.getViewport({ scale: 1 });
    setPageSize({ width: unscaled.width, height: unscaled.height });

    const maxWidth = Math.min(720, window.innerWidth - 48);
    const scale = maxWidth / unscaled.width;
    const viewport = pdfPage.getViewport({ scale });
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    setPreviewSize({ width: viewport.width, height: viewport.height });
    setStamp((prev) => ({
      ...prev,
      x: clamp(prev.x, 0, Math.max(0, viewport.width - prev.width)),
      y: clamp(prev.y, 0, Math.max(0, viewport.height - prev.height))
    }));
    await pdfPage.render({ canvasContext: context, viewport }).promise;
  }, []);

  const onPdfSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setError('');
    setStatus('');
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please choose a PDF file');
      return;
    }
    if (file.size > MAX_PDF_MB * 1024 * 1024) {
      setError(`PDF must be ${MAX_PDF_MB}MB or smaller`);
      return;
    }

    try {
      setBusy(true);
      const data = new Uint8Array(await file.arrayBuffer());
      const doc = await pdfjsLib.getDocument({ data }).promise;
      pdfDocRef.current = doc;
      setPdfFile(file);
      setPageCount(doc.numPages);
      setPage(1);
      await renderPage(doc, 1);
      setStatus(`Loaded ${file.name} (${doc.numPages} page${doc.numPages === 1 ? '' : 's'}) — drag or click to place your signature`);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not open PDF');
      setPdfFile(null);
      pdfDocRef.current = null;
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!pdfDocRef.current || !page) return;
    renderPage(pdfDocRef.current, page).catch((err) => {
      setError(err.message || 'Could not render page');
    });
  }, [page, renderPage]);

  const captureDrawPad = () => {
    const canvas = drawPadRef.current;
    if (!canvas) return '';
    return canvas.toDataURL('image/png');
  };

  const captureTypedSignature = () => {
    const text = typedName.trim();
    if (!text) return '';
    const canvas = document.createElement('canvas');
    canvas.width = 520;
    canvas.height = 160;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#0f172a';
    ctx.font = 'italic 56px "Segoe Script", "Brush Script MT", cursive';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 24, canvas.height / 2);
    return canvas.toDataURL('image/png');
  };

  const useCurrentSignature = async () => {
    setError('');
    let dataUrl = '';
    if (mode === 'draw') dataUrl = captureDrawPad();
    else if (mode === 'type') dataUrl = captureTypedSignature();
    else dataUrl = signatureDataUrl;

    if (!dataUrl || dataUrl.length < 100) {
      setError(mode === 'type' ? 'Type your name first' : 'Create or upload a signature first');
      return;
    }
    try {
      dataUrl = await makeInkTransparent(dataUrl);
    } catch {
      // keep original if stripping fails
    }
    setSignatureDataUrl(dataUrl);
    saveSignatureDataUrl(dataUrl);
    setStatus(pdfFile ? 'Signature ready — drag it on the PDF or click to place' : 'Signature ready — upload a PDF next');
  };

  const removeSignature = () => {
    setSignatureDataUrl('');
    clearSavedSignature();
    setDragging(false);
    setError('');
    setStatus('Signature removed from the page');
  };

  const onSignatureUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Signature upload must be an image');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const img = await loadImage(String(reader.result));
        const canvas = document.createElement('canvas');
        canvas.width = Math.min(800, img.width);
        canvas.height = Math.max(1, Math.round((img.height / img.width) * canvas.width));
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = await makeInkTransparent(canvas.toDataURL('image/png'));
        setSignatureDataUrl(dataUrl);
        saveSignatureDataUrl(dataUrl);
        setStatus('Signature image loaded');
      } catch {
        setError('Could not load signature image');
      }
    };
    reader.readAsDataURL(file);
  };

  const startDraw = (event) => {
    const canvas = drawPadRef.current;
    if (!canvas) return;
    drawingRef.current = true;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) * (canvas.width / rect.width);
    const y = (event.clientY - rect.top) * (canvas.height / rect.height);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveDraw = (event) => {
    if (!drawingRef.current) return;
    const canvas = drawPadRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) * (canvas.width / rect.width);
    const y = (event.clientY - rect.top) * (canvas.height / rect.height);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDraw = () => {
    drawingRef.current = false;
  };

  const onStampPointerDown = (event) => {
    if (!signatureDataUrl) return;
    event.preventDefault();
    event.stopPropagation();
    const wrap = event.currentTarget.parentElement;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    setDragging(true);
    setDragOffset({
      x: event.clientX - rect.left - stamp.x,
      y: event.clientY - rect.top - stamp.y
    });
  };

  const placeStampAtClient = (clientX, clientY) => {
    const wrap = canvasRef.current?.parentElement;
    if (!wrap || !previewSize.width) return;
    const rect = wrap.getBoundingClientRect();
    setStamp((prev) => ({
      ...prev,
      x: clamp(clientX - rect.left - prev.width / 2, 0, Math.max(0, previewSize.width - prev.width)),
      y: clamp(clientY - rect.top - prev.height / 2, 0, Math.max(0, previewSize.height - prev.height))
    }));
  };

  const onPreviewPointerDown = (event) => {
    if (!pdfFile || !signatureDataUrl || dragging) return;
    if (event.target.closest('.sign-pdf-stamp')) return;
    placeStampAtClient(event.clientX, event.clientY);
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (event) => {
      const wrap = canvasRef.current?.parentElement;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      setStamp((prev) => ({
        ...prev,
        x: clamp(event.clientX - rect.left - dragOffset.x, 0, Math.max(0, previewSize.width - prev.width)),
        y: clamp(event.clientY - rect.top - dragOffset.y, 0, Math.max(0, previewSize.height - prev.height))
      }));
    };
    const onUp = () => setDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragging, dragOffset, previewSize]);

  const applySignature = async () => {
    setError('');
    setStatus('');
    if (!pdfFile) {
      setError('Upload a PDF first');
      return;
    }
    if (!signatureDataUrl) {
      setError('Create a signature and click “Use signature”');
      return;
    }
    if (!pageSize.width || !previewSize.width) {
      setError('PDF preview is not ready');
      return;
    }

    const scaleX = pageSize.width / previewSize.width;
    const scaleY = pageSize.height / previewSize.height;

    try {
      setBusy(true);
      const cleanSig = await makeInkTransparent(signatureDataUrl);
      const baked = await bakeRotatedSignature(
        cleanSig,
        stamp.width,
        stamp.height,
        stamp.angle
      );

      // Keep stamp center fixed when applying baked (axis-aligned) bounds.
      const centerX = stamp.x + stamp.width / 2;
      const centerY = stamp.y + stamp.height / 2;
      const previewLeft = centerX - baked.width / 2;
      const previewTop = centerY - baked.height / 2;

      const width = baked.width * scaleX;
      const height = baked.height * scaleY;
      const x = previewLeft * scaleX;
      const y = pageSize.height - previewTop * scaleY - height;

      const result = await signPdf({
        pdfFile,
        signatureBlob: baked.blob,
        page,
        x,
        y,
        width,
        height,
        angle: 0
      });
      rememberRecentApp('sign-pdf');
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      setStatus(`Signed PDF downloaded${result.sha256 ? ` · ${result.sha256.slice(0, 12)}…` : ''}`);
    } catch (err) {
      setError(err.message || 'Could not sign PDF');
    } finally {
      setBusy(false);
    }
  };

  const nudgeDisabled = !pdfFile || !signatureDataUrl;

  return (
    <div className="sign-pdf-page">
      <header className="sign-pdf-topbar">
        <button type="button" className="sign-pdf-back" onClick={() => navigate('/apps')}>
          <FiArrowLeft aria-hidden="true" /> <span>Back to My Apps</span>
        </button>
        <div className="sign-pdf-topbar-actions">
          <HelpMenu onTour={startTour} />
          <FeedbackButton />
          <TourButton onClick={startTour} />
          <ThemeToggle compact />
        </div>
      </header>

      <div className="sign-pdf-shell">
        <header className="sign-pdf-header">
          <span className="sign-pdf-hero-icon" aria-hidden="true"><FiEdit3 /></span>
          <div>
            <span className="sign-pdf-kicker">Office tools</span>
            <h1>Sign PDF</h1>
            <p className="sign-pdf-disclaimer">
              <FiInfo aria-hidden="true" />
              Visual stamp for internal use only. This is not a certificate-based digital signature.
            </p>
          </div>
        </header>

        {error && (
          <div className="sign-pdf-alert is-error" role="alert">
            <FiAlertCircle aria-hidden="true" /> <span>{error}</span>
          </div>
        )}
        {status && !error && (
          <div className="sign-pdf-alert is-ok">
            <FiCheckCircle aria-hidden="true" /> <span>{status}</span>
          </div>
        )}

        <div className="sign-pdf-layout">
          <section className="sign-pdf-panel">
            <div className="sign-pdf-step" data-tour="sign-upload">
              <h2><span className={`sign-pdf-step-num ${pdfFile ? 'is-done' : ''}`}>{pdfFile ? <FiCheck aria-hidden="true" /> : '1'}</span> PDF</h2>
              <FileDrop
                className="sign-pdf-file"
                accept="application/pdf,.pdf"
                disabled={busy}
                title={pdfFile ? 'Choose a different PDF' : 'Choose PDF'}
                hint={`or drop it here · up to ${MAX_PDF_MB}MB`}
                onInputChange={onPdfSelected}
              />
              {pdfFile && (
                <p className="sign-pdf-file-name"><FiFileText aria-hidden="true" /> <span>{pdfFile.name}</span></p>
              )}
              {pageCount > 1 && (
                <div className="sign-pdf-page-nav">
                  <button type="button" disabled={page <= 1 || busy} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                    <FiChevronLeft aria-hidden="true" /> Previous
                  </button>
                  <span>Page {page} / {pageCount}</span>
                  <button type="button" disabled={page >= pageCount || busy} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                    Next <FiChevronRight aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>

            <div className="sign-pdf-step" data-tour="sign-signature">
              <h2><span className={`sign-pdf-step-num ${signatureDataUrl ? 'is-done' : ''}`}>{signatureDataUrl ? <FiCheck aria-hidden="true" /> : '2'}</span> Signature</h2>
              <div className="sign-pdf-modes" role="tablist" aria-label="Signature method">
                {[
                  { id: 'draw', label: 'Draw', icon: FiEdit2 },
                  { id: 'type', label: 'Type', icon: FiType },
                  { id: 'upload', label: 'Upload', icon: FiImage }
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={mode === item.id}
                    className={mode === item.id ? 'is-active' : ''}
                    onClick={() => setMode(item.id)}
                  >
                    <item.icon aria-hidden="true" /> {item.label}
                  </button>
                ))}
              </div>

              <div className="sign-pdf-mode-body" key={mode}>
                {mode === 'draw' && (
                  <div className="sign-pdf-draw-wrap">
                    <canvas
                      ref={drawPadRef}
                      width={480}
                      height={160}
                      className="sign-pdf-draw-pad"
                      onPointerDown={startDraw}
                      onPointerMove={moveDraw}
                      onPointerUp={endDraw}
                      onPointerLeave={endDraw}
                    />
                    <button type="button" className="sign-pdf-secondary sign-pdf-clear" onClick={clearDrawPad}>
                      <FiRotateCcw aria-hidden="true" /> Clear
                    </button>
                  </div>
                )}

                {mode === 'type' && (
                  <input
                    className="sign-pdf-type-input"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Type your full name"
                    maxLength={80}
                  />
                )}

                {mode === 'upload' && (
                  <FileDrop
                    className="sign-pdf-file"
                    accept="image/png,image/jpeg,image/webp"
                    title="Upload signature image"
                    hint="PNG, JPG or WebP — white background is removed"
                    onInputChange={onSignatureUpload}
                  />
                )}
              </div>

              <div className="sign-pdf-sig-actions">
                <button type="button" className="sign-pdf-primary" onClick={useCurrentSignature} disabled={busy}>
                  <FiCheck aria-hidden="true" /> Use signature
                </button>
                {signatureDataUrl && (
                  <button type="button" className="sign-pdf-secondary sign-pdf-remove" onClick={removeSignature} disabled={busy}>
                    <FiTrash2 aria-hidden="true" /> Remove signature
                  </button>
                )}
              </div>

              {signatureDataUrl && (
                <div className="sign-pdf-sig-preview">
                  <img src={signatureDataUrl} alt="Signature preview" />
                </div>
              )}
            </div>

            <div className="sign-pdf-step" data-tour="sign-adjust">
              <h2><span className="sign-pdf-step-num">3</span> Size, angle &amp; placement</h2>
              <label className="sign-pdf-range">
                <span>Width <b>{stamp.width}px</b></span>
                <input
                  type="range"
                  min="80"
                  max="360"
                  value={stamp.width}
                  onChange={(e) => setStamp((s) => ({ ...s, width: Number(e.target.value) }))}
                />
              </label>
              <label className="sign-pdf-range">
                <span>Height <b>{stamp.height}px</b></span>
                <input
                  type="range"
                  min="36"
                  max="160"
                  value={stamp.height}
                  onChange={(e) => setStamp((s) => ({ ...s, height: Number(e.target.value) }))}
                />
              </label>
              <label className="sign-pdf-range">
                <span>Angle <b>{stamp.angle}°</b></span>
                <input
                  type="range"
                  min="-45"
                  max="45"
                  step="1"
                  value={stamp.angle}
                  onChange={(e) => setStamp((s) => ({ ...s, angle: Number(e.target.value) }))}
                />
              </label>
              <div className="sign-pdf-nudge">
                <button
                  type="button"
                  className="sign-pdf-secondary"
                  aria-label="Move left"
                  disabled={nudgeDisabled}
                  onClick={() => setStamp((s) => ({ ...s, x: clamp(s.x - 8, 0, Math.max(0, previewSize.width - s.width)) }))}
                >
                  <FiArrowLeft aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="sign-pdf-secondary"
                  aria-label="Move up"
                  disabled={nudgeDisabled}
                  onClick={() => setStamp((s) => ({ ...s, y: clamp(s.y - 8, 0, Math.max(0, previewSize.height - s.height)) }))}
                >
                  <FiArrowUp aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="sign-pdf-secondary"
                  aria-label="Move down"
                  disabled={nudgeDisabled}
                  onClick={() => setStamp((s) => ({ ...s, y: clamp(s.y + 8, 0, Math.max(0, previewSize.height - s.height)) }))}
                >
                  <FiArrowDown aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="sign-pdf-secondary"
                  aria-label="Move right"
                  disabled={nudgeDisabled}
                  onClick={() => setStamp((s) => ({ ...s, x: clamp(s.x + 8, 0, Math.max(0, previewSize.width - s.width)) }))}
                >
                  <FiArrowRight aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="sign-pdf-secondary sign-pdf-reset"
                  disabled={!signatureDataUrl}
                  onClick={() => setStamp((s) => ({ ...s, angle: 0 }))}
                >
                  <FiRotateCcw aria-hidden="true" /> Reset angle
                </button>
              </div>
            </div>

            <button
              type="button"
              className="sign-pdf-primary sign-pdf-apply"
              onClick={applySignature}
              disabled={busy || !pdfFile || !signatureDataUrl}
              data-tour="sign-apply"
            >
              {busy ? <span className="sign-pdf-spinner" aria-hidden="true" /> : <FiDownload aria-hidden="true" />}
              {busy ? 'Working…' : 'Apply & download'}
            </button>
          </section>

          <section className={`sign-pdf-preview-panel ${busy ? 'is-busy' : ''}`} data-tour="sign-preview">
            <h2><FiMove aria-hidden="true" /> Preview — drag or click to place signature</h2>
            <div
              className={`sign-pdf-canvas-wrap ${pdfFile ? 'has-pdf' : ''} ${dragging ? 'is-dragging' : ''}`}
              onPointerDown={onPreviewPointerDown}
            >
              {!pdfFile && (
                <div className="sign-pdf-empty">
                  <span className="sign-pdf-empty-icon" aria-hidden="true"><FiFileText /></span>
                  <strong>No PDF yet</strong>
                  <span>Upload a PDF to preview pages here</span>
                </div>
              )}
              <canvas ref={canvasRef} className="sign-pdf-canvas" />
              {pdfFile && signatureDataUrl && (
                <div
                  className="sign-pdf-stamp"
                  style={{
                    left: stamp.x,
                    top: stamp.y,
                    width: stamp.width,
                    height: stamp.height,
                    transform: `rotate(${stamp.angle}deg)`
                  }}
                  onPointerDown={onStampPointerDown}
                  role="img"
                  aria-label="Signature stamp — drag to move"
                >
                  <button
                    type="button"
                    className="sign-pdf-stamp-remove"
                    aria-label="Remove signature"
                    title="Remove signature"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSignature();
                    }}
                  >
                    <FiX aria-hidden="true" />
                  </button>
                  <img src={signatureDataUrl} alt="" draggable={false} />
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
      {touring && <TourMode steps={SIGN_TOUR_STEPS} topOffset={64} onClose={endTour} />}
    </div>
  );
}
